import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import integrationService from "../../services/integrationService";
import { LocalStorageService } from "../../services/cookieService";
import { logout } from "./authSlice";

const CACHE_DURATION = 10 * 60 * 1000;
const POLLING_INTERVAL = 10 * 1000;

export const fetchIntegrations =
  createAsyncThunk(
    "integrations/fetchIntegrations",
    async (_, { getState, rejectWithValue }) => {

      try {
        const { lastFetched } = getState().integrations;
        const now = Date.now();

        if ( lastFetched && now - lastFetched < CACHE_DURATION ) {
          return { cached: true };
        }
        const data = await integrationService.getIntegrations();
        return { cached: false, data };

      } catch (error) {
        return rejectWithValue(
          error?.message ||
          "Failed to load integrations"
        );
      }
    }
  );


export const syncIntegration =
  createAsyncThunk( "integrations/syncIntegration",
    async ( { integrationId }, { dispatch, rejectWithValue } ) => {
      try {
        const response = await integrationService.syncIntegration( integrationId );
        const status = response.status ?? "PENDING";
        const taskId = response.task_id;
        const message = response.message
        LocalStorageService.setTaskId(taskId, integrationId);
        LocalStorageService.setTaskStatus(status, integrationId);
        LocalStorageService.setTaskMessage(message, integrationId);

        if ( status === "PENDING" && taskId ) {
          dispatch( startPollingTaskStatus({ integrationId, taskId }) );
        }

        return {
          ...response
        };

      } catch (error) {

        return rejectWithValue({
          integrationId,
          error:
            error?.message ||
            "Sync failed"
        });
      }
    }
  );



/* =========================
   START POLLING
========================= */

export const startPollingTaskStatus = createAsyncThunk( "integrations/startPollingTaskStatus",
    async ( { integrationId, taskId }, { dispatch, rejectWithValue } ) => {
      try {
        const currentStatus = LocalStorageService.getTaskStatus(integrationId)
        if (currentStatus === "SUCCESS") return;

        return await dispatch( checkTaskStatusWithPolling({ integrationId, taskId }) );

      } catch (error) {

        return rejectWithValue({
          integrationId,
          error:
            error?.message ||
            "Failed to start polling"
        });
      }
    }
  );



/* =========================
   POLLING THUNK
========================= */

export const checkTaskStatusWithPolling = createAsyncThunk( "integrations/checkTaskStatusWithPolling",
    async ( { integrationId, taskId }, { dispatch, rejectWithValue } ) => {
      try {
        const response = await integrationService.getTaskStatus(taskId);
        const status = response.status;

        LocalStorageService.setTaskStatus(status, integrationId)
        const taskStatus = {
          taskId,
          integrationId,
          status,
          result: response.result,
          message:
            response.message ||
            `Status: ${status}`
        };

        if (status === "PENDING") {
          setTimeout(() => {
            dispatch( checkTaskStatusWithPolling({ integrationId, taskId }) );
          }, POLLING_INTERVAL);

          return taskStatus;
        }

        /* SUCCESS */
        if (status === "SUCCESS") {
          dispatch( fetchIntegrations() );

          setTimeout(() => {
            dispatch( clearSyncStatusFromState( integrationId ) );
          }, 3000);

          LocalStorageService.setTaskStatus( "SUCCESS", integrationId );

          return {
            ...taskStatus,
            message:
              response.message ||
              "Sync completed successfully"
          };
        }

        /* FAILURE */
        if (status === "FAILURE") {

          return {
            ...taskStatus,
            message:
              response.message ||
              "Sync failed"
          };
        }

        return taskStatus;

      } catch (error) {

        return rejectWithValue({
          taskId,
          integrationId,
          error:
            error?.message ||
            "Task status check failed"
        });
      }
    }
  );



/* =========================
   MANUAL STATUS CHECK
========================= */

export const checkTaskStatus = createAsyncThunk( "integrations/checkTaskStatus",
    async ( { taskId, integrationId }, { dispatch, rejectWithValue } ) => {
      try {
        const response = await integrationService.getTaskStatus(taskId);
        const status = response.status;

        if (status === "SUCCESS") {
          dispatch( fetchIntegrations() );
          LocalStorageService.setTaskStatus( status, integrationId );
        }

        return {
          taskId,
          integrationId,
          status,
          ...response
        };

      } catch (error) {

        return rejectWithValue({
          taskId,
          integrationId,
          error:
            error?.message ||
            "Task status check failed"
        });
      }
    }
);



/* =========================
   TEST CONNECTION
========================= */

export const testConnection = createAsyncThunk( "integrations/testConnection",
    async ( integrationId, { rejectWithValue } ) => {
      try {
        const response = await integrationService.testConnection( integrationId );

        return {
          integrationId,
          ...response
        };

      } catch (error) {

        return rejectWithValue({
          integrationId,
          error:
            error?.message ||
            "Connection test failed"
        });
      }
    }
);



/* =========================
   SLICE
========================= */

const integrationsSlice = createSlice({
    name: "integrations",
    initialState: {
      integrations: [],
      loading: false,
      error: null,
      lastFetched: null,
      activeTasks: {},
      connectionTests: {},
      refreshing: false,
    },

    reducers: {
      clearSyncStatusFromState: (state, action) => {
          delete state.activeTasks[
            action.payload
          ];
        },
    },

    extraReducers: (builder) => {
      builder

        /* FETCH */
        .addCase(
          fetchIntegrations.pending, (state) => {
            state.loading = true;
            state.error = null;
          }
        )

        .addCase(
          fetchIntegrations.fulfilled, (state, action) => {
            state.loading = false;

            const {
              cached,
              data
            } = action.payload;

            if (!cached) {
              state.integrations = data;
              state.lastFetched = Date.now();
            }
          }
        )

        .addCase( fetchIntegrations.rejected, (state, action) => {
            state.loading = false;
            state.error =
              action.payload;
          }
        )

        /* SYNC */
        .addCase( syncIntegration.pending, (state, action) => {
            const { integrationId } = action.meta.arg;

            state.activeTasks[ integrationId ] = {
              status: "PENDING",
              message:
                "Starting sync...",
              startTime:
                Date.now(),
            };
          }
        )

        .addCase( syncIntegration.fulfilled, (state, action) => {
            const { integrationId, taskId, status, message } = action.payload;
            state.activeTasks[ integrationId ] = {
              taskId,
              status,
              message,
              polling: true,
              startTime: Date.now(),
            };
          }
        )

        .addCase( syncIntegration.rejected, (state, action) => {
            const integrationId = action.payload?.integrationId;

            if (!integrationId) return;

            state.activeTasks[ integrationId ] = {
              status: "FAILED",
              error:
                action.payload
                  ?.error,

              message:
                action.payload
                  ?.error,
            };
          }
        )



        /* POLLING */
        .addCase(
          checkTaskStatusWithPolling.fulfilled, (state, action) => {
            const { integrationId, status, result, message } = action.payload;

            if (state.activeTasks[integrationId]) {
              state.activeTasks[integrationId] = {
                ...state.activeTasks[integrationId],
                status, result, message, lastChecked: Date.now(),
                polling: status === "PENDING",
              };
            }
          }
        )



        /* TEST CONNECTION */
        .addCase( testConnection.pending, (state, action) => {
            const integrationId = action.meta.arg;
            state.connectionTests[ integrationId ] = {
              testing: true
            };
          }
        )

        .addCase( testConnection.fulfilled, (state, action) => {
            const { integrationId, ...result } = action.payload;
            state.connectionTests[ integrationId ] = {
              testing: false,
              result
            };
          }
        )

        .addCase( testConnection.rejected, (state, action) => {
            const { integrationId, error } = action.payload;
            state.connectionTests[ integrationId ] = {
              testing: false,
              error
            };
          }
        )

        /* LOGOUT */
        .addCase( logout, (state) => {
            state.activeTasks = {};
            state.integrations = [];
            state.lastFetched = null;
          }
        );
    }
});

export const { clearSyncStatusFromState } = integrationsSlice.actions;

export default integrationsSlice.reducer;